import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MyResults.css';

const TYPE_LABELS = { practice: 'Practice', mini: 'Mini Mock', full: 'Full Mock' };

function MyResults({ user, onBack, onRetakePractice, onRetakeMiniMock, onRetakeFullMock, apiUrl, embedded = false }) {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [practice, mini, full] = await Promise.all([
        axios.get(`${apiUrl}/practice/attempts`).then((r) => r.data).catch(() => []),
        axios.get(`${apiUrl}/mini-mocks/attempts`).then((r) => r.data).catch(() => []),
        axios.get(`${apiUrl}/full-mocks/attempts`).then((r) => r.data).catch(() => []),
      ]);

      const merged = [
        ...practice.map((a) => ({
          ...a,
          type: 'practice',
          title: `${a.section} Practice${a.topic_family ? ' – ' + formatTopicName(a.topic_family) : ' – All Topics'}`,
        })),
        ...mini.map((a) => ({ ...a, type: 'mini', title: a.title, refId: a.mini_mock_id })),
        ...full.map((a) => ({ ...a, type: 'full', title: a.title, refId: a.full_mock_id })),
      ].sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

      setAttempts(merged);
    } catch (err) {
      setError('Failed to load past results');
    } finally {
      setLoading(false);
    }
  };

  const endpointFor = (type) => (type === 'practice' ? 'practice' : type === 'mini' ? 'mini-mocks' : 'full-mocks');

  const openDetail = async (item) => {
    setSelected(item);
    setDetail(null);
    setDetailLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/${endpointFor(item.type)}/attempts/${item.id}`);
      setDetail(response.data);
    } catch (err) {
      setError('Failed to load attempt detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRetake = () => {
    if (!selected) return;
    if (selected.type === 'practice') onRetakePractice(selected.section);
    else if (selected.type === 'mini') onRetakeMiniMock(selected.refId);
    else onRetakeFullMock(selected.refId);
  };

  const getOptions = (options) => {
    if (!options) return [];
    return typeof options === 'string' ? JSON.parse(options) : options;
  };

  if (selected) {
    const detailBody = (
      <>
        {error && <div className="error">{error}</div>}
          {detailLoading ? (
            <div className="loading"><div className="spinner"></div>Loading review...</div>
          ) : (
            <>
              {detail?.attempt?.feedback_summary && (
                <div className="feedback-box">
                  <h3>Your Personalised Feedback</h3>
                  {detail.attempt.feedback_summary.split('\n\n').map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
              )}
              <div className="review-list">
              {detail?.answers?.map((a, idx) => {
                const options = getOptions(a.options);
                return (
                  <div key={a.id} className="review-item">
                    <div className="review-item-header">
                      <span>Question {idx + 1}</span>
                      <span className={a.is_correct ? 'review-badge review-badge-correct' : 'review-badge review-badge-incorrect'}>
                        {a.is_correct ? 'Correct' : 'Incorrect'}
                      </span>
                    </div>
                    <p className="question-text">{a.question_text}</p>
                    {options.length > 0 ? (
                      <div className="options">
                        {options.map((option, i) => {
                          const isYourAnswer = option === a.selected_answer;
                          const isCorrectOption = option === a.correct_answer;
                          return (
                            <div
                              key={i}
                              className={[
                                'option',
                                isCorrectOption ? 'correct' : '',
                                isYourAnswer && !isCorrectOption ? 'incorrect' : '',
                              ].join(' ').trim()}
                            >
                              {option}
                              {isYourAnswer && <span className="your-answer-tag"> (your answer)</span>}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p>
                        <strong>Your answer:</strong> {a.selected_answer || '(not answered)'}<br />
                        <strong>Correct answer:</strong> {a.correct_answer}
                      </p>
                    )}
                    <div className="explanation">
                      <strong>Explanation:</strong> {a.explanation}
                    </div>
                  </div>
                );
              })}
              {detail && detail.answers.length === 0 && <p>No answers were recorded for this attempt.</p>}
              </div>
            </>
          )}
      </>
    );

    const detailHeaderActions = (
      <>
        <button className="btn btn-secondary" onClick={() => { setSelected(null); setDetail(null); }}>← Back to Results</button>
        <button className="btn btn-primary" onClick={handleRetake}>Retake</button>
      </>
    );

    if (embedded) {
      return (
        <div className="my-results my-results-embedded">
          <div className="panel-header-row">
            <div>
              <h2>{selected.title}</h2>
              <p className="header-subtitle">
                {selected.correct_answers} / {selected.total_questions} correct · {Math.round(selected.score)}%
                {' · '}{new Date(selected.completed_at).toLocaleString()}
              </p>
            </div>
            <div className="header-actions">{detailHeaderActions}</div>
          </div>
          {detailBody}
        </div>
      );
    }

    return (
      <div className="container my-results">
        <div className="header">
          <div>
            <h1>{selected.title}</h1>
            <p className="header-subtitle">
              {selected.correct_answers} / {selected.total_questions} correct · {Math.round(selected.score)}%
              {' · '}{new Date(selected.completed_at).toLocaleString()}
            </p>
          </div>
          <div className="header-actions">{detailHeaderActions}</div>
        </div>
        <div className="content">{detailBody}</div>
      </div>
    );
  }

  const listBody = (
    <>
      {error && <div className="error">{error}</div>}
        {loading ? (
          <div className="loading"><div className="spinner"></div>Loading your results...</div>
        ) : attempts.length === 0 ? (
          <div className="empty-state"><p>No completed sessions yet. Finish a practice session or exam to see it here.</p></div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Score</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <tr key={`${a.type}-${a.id}`}>
                    <td>{TYPE_LABELS[a.type]}</td>
                    <td>{a.title}</td>
                    <td>{a.correct_answers}/{a.total_questions} ({Math.round(a.score)}%)</td>
                    <td>{new Date(a.completed_at).toLocaleDateString()}</td>
                    <td><button className="btn btn-secondary" onClick={() => openDetail(a)}>Review</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className="my-results my-results-embedded">
        <div className="panel-header-row">
          <div>
            <h2>My Results</h2>
            <p className="header-subtitle">Past practice sessions and mock exams</p>
          </div>
        </div>
        {listBody}
      </div>
    );
  }

  return (
    <div className="container my-results">
      <div className="header">
        <div>
          <h1>My Results</h1>
          <p className="header-subtitle">Past practice sessions and mock exams</p>
        </div>
        <button className="btn btn-secondary" onClick={onBack}>← Back to Dashboard</button>
      </div>
      <div className="content">{listBody}</div>
    </div>
  );
}

function formatTopicName(family) {
  return (family || '')
    .replace(/^fam_/, '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default MyResults;
